import api from '../api';
import {parseZodSchema} from '../models/parseZodSchema';
import {
    desklyConnectSettings,
    type DesklyConnectSettings,
    type DesklyConnectForm,
} from '../models/desklyConnect/desklyConnectFormModel';
import {
    desklyConnectUsageStatistics,
    type DesklyConnectUsageStatistics,
} from '../models/desklyConnect/desklyConnectUsageModel';
import {
    resourceConnection,
    type ResourceConnection,
} from '../models/desklyConnect/resourceConnectionModel';
import {
    adminLocationWithConnections,
    type AdminLocationWithConnections,
} from '../models/desklyConnect/spaceConnectionModels';
import * as z from 'zod';

export default () => ({
    read(accountId: string): Promise<DesklyConnectSettings> {
        return api
            .get(`/api/admin/deskly-connect/${accountId}`)
            .then(api.handle)
            .catch(api.error)
            .then(raw => parseZodSchema(raw, desklyConnectSettings));
    },

    update(accountId: string, settings: DesklyConnectForm): Promise<DesklyConnectSettings> {
        return api
            .put(`/api/admin/deskly-connect/${accountId}`, settings)
            .then(api.handle)
            .catch(api.error)
            .then(raw => parseZodSchema(raw, desklyConnectSettings));
    },

    readUsageStatistics(accountId: string): Promise<DesklyConnectUsageStatistics> {
        return api
            .get(`/api/admin/statistics/deskly-connect-usage/${accountId}`)
            .then(api.handle)
            .catch(api.error)
            .then(raw => parseZodSchema(raw, desklyConnectUsageStatistics));
    },

    authorizeSensor(
        sensorId: string,
        target?: string
    ): Promise<{success: boolean; message: string}> {
        const queryParams = new URLSearchParams({sensorId});
        if (target) {
            queryParams.append('target', target);
        }

        return api
            .post(`/api/deskly-connect/authorize-sensor?${queryParams.toString()}`, null)
            .then(api.handle)
            .catch(api.error);
    },

    connectionList(roomId: string, target?: string): Promise<ResourceConnection[]> {
        let url = `/api/admin/deskly-connect/resource/connection/list/${roomId}`;
        if (target != null) {
            url += `?target=${encodeURIComponent(target)}`;
        }
        return api
            .get(url)
            .then(api.handle)
            .catch(api.error)
            .then(raw => parseZodSchema(raw, z.array(resourceConnection)));
    },

    connectResource(
        resourceId: string,
        sensorId: string,
        target?: string
    ): Promise<{success: boolean; message: string}> {
        const queryParams = new URLSearchParams({sensorId});
        if (target) {
            queryParams.append('target', target);
        }
        return api
            .post(
                `/api/admin/deskly-connect/connect-resource/${resourceId}?${queryParams.toString()}`,
                null
            )
            .then(api.handle)
            .catch(api.error);
    },

    unpairResource(
        resourceId: string,
        target?: string
    ): Promise<{success: boolean; message: string}> {
        const queryParams = new URLSearchParams();
        if (target) {
            queryParams.append('target', target);
        }
        return api
            .post(
                `/api/admin/deskly-connect/unpair-resource/${resourceId}?${queryParams.toString()}`,
                null
            )
            .then(api.handle)
            .catch(api.error);
    },

    spaceStructureWithConnections(
        accountId: string,
        target?: string
    ): Promise<AdminLocationWithConnections[]> {
        let url = `/api/admin/deskly-connect/space-structure-with-connections/${accountId}`;
        if (target != null) {
            url += `?target=${encodeURIComponent(target)}`;
        }
        return api
            .get(url)
            .then(api.handle)
            .catch(api.error)
            .then(raw => parseZodSchema(raw, z.array(adminLocationWithConnections)));
    },
});
